import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import moment from "moment";
import { LazyLoadImage } from "react-lazy-load-image-component";
import api from "../../../../api/api";
import { fetchIndices } from "../../Indices/actions";
import HistoryDateFilter from "./HistoryDateFilter";
import { getPathList, getLessonEditUrl, getIndexListUrl } from "./historyUtils";

const UserLessonHistoryModal = ({ show, onClose, user, t, indices, fetchIndices }) => {
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(moment().subtract(6, 'days'));
    const [endDate, setEndDate] = useState(moment());
    const [searchText, setSearchText] = useState("");
    const [expandedIds, setExpandedIds] = useState({});
    const [searchMode, setSearchMode] = useState("date");
    const [count, setCount] = useState(20);

    const fetchData = (currentMode = searchMode, start = startDate, end = endDate, cnt = count) => {
        if (!user) return;
        setLoading(true);

        if (!indices || !indices.nodes || indices.nodes.length === 0) {
            fetchIndices();
        }

        let url = "";
        if (currentMode === "date") {
            url = `/UserLessonHistory?userId=${user.Id}&startDate=${start.format('DD/MM/YYYY')}&endDate=${end.format('DD/MM/YYYY')}&count=1000&groupLesson=true&searchMode=date`;
        } else {
            url = `/UserLessonHistory?userId=${user.Id}&count=${cnt}&groupLesson=true&searchMode=count`;
        }

        api.get(url)
            .then(resp => {
                setLessons(resp.data?.data || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (show && user) {
            fetchData();
        }
    }, [show, user, indices, fetchIndices]);

    // Client-side filtering
    const filteredLessons = lessons.map(lesson => {
        const filteredAttempts = lesson.Attempts?.filter(attempt => {
            if (searchMode === "date") {
                if (!attempt.CreationDate) return false;
                const attemptDate = moment(attempt.CreationDate, "YYYY-MM-DD HH:mm");
                return attemptDate.isSameOrAfter(startDate, 'day') && attemptDate.isSameOrBefore(endDate, 'day');
            }
            return true;
        }) || [];
        return {
            ...lesson,
            Attempts: filteredAttempts
        };
    }).filter(lesson => {
        if (lesson.Attempts.length === 0) return false;

        if (searchText) {
            const query = searchText.toLowerCase();
            const lessonName = lesson.Name ? lesson.Name.toLowerCase() : (lesson.title ? lesson.title.toLowerCase() : "");
            if (!lessonName.includes(query)) return false;
        }

        return true;
    });

    const toggleExpanded = (id) => {
        setExpandedIds(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="admin-history-modal" enforceFocus={false}>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-primary">
                            <i className="fad fa-book-open"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">
                                {t("Pages.UsersControl.action.studyHistory") || "سجل الدراسة"}
                            </div>
                            <div className="small text-muted">{user?.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                {/* Date Filter */}
                <HistoryDateFilter
                    startDate={startDate}
                    endDate={endDate}
                    searchMode={searchMode}
                    count={count}
                    searchText={searchText}
                    showSearchModeSelect={true}
                    onApply={(mode, start, end, cnt, search) => {
                        setSearchMode(mode);
                        setStartDate(start);
                        setEndDate(end);
                        setCount(cnt);
                        setSearchText(search);
                        fetchData(mode, start, end, cnt);
                    }}
                    t={t}
                />

                {loading ? (
                    <div className="text-center py-5">
                        <i className="fad fa-sync fa-spin fa-2x text-muted mb-2"></i>
                        <div className="text-muted small">{t("Pages.UsersControl.Loading") || "جاري التحميل..."}</div>
                    </div>
                ) : filteredLessons.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fal fa-file-search fa-2x mb-2 d-block"></i>
                        <span>{t("Pages.UsersControl.NotFound") || "لا توجد نتائج"}</span>
                    </div>
                ) : (
                    <div className="history-modal-list scrollbar-custom mx-0" style={{ direction: 'rtl' }}>
                        {filteredLessons.map((lesson, idx) => {
                            const pathList = getPathList(indices?.nodes, lesson.Index?.Id, 0);
                            const isExpanded = !!expandedIds[lesson.Id || idx];

                            return (
                                <div key={lesson.Id || idx} className="w-100 mb-3 compact-history-card">
                                    <div className="compact-history-row">
                                        <div className="compact-history-left">
                                            <div className="compact-history-icon-box p-0 overflow-hidden" style={{ background: '#f8fafc' }}>
                                                <LazyLoadImage
                                                    src={lesson.Icon || "/assets/images/logo/130p150.png"}
                                                    alt={lesson.Name}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                            <div className="compact-history-info">
                                                <div className="d-flex align-items-center flex-wrap gap-2">
                                                    <h6 className="compact-history-title mb-0">{lesson.Name}</h6>
                                                    {getLessonEditUrl(indices, lesson) && (
                                                        <Link to={getLessonEditUrl(indices, lesson)} className="btn-link text-primary ml-2" onClick={onClose} title={t("View") || "عرض"}>
                                                            <i className="fal fa-external-link" style={{ fontSize: '0.85rem' }}></i>
                                                        </Link>
                                                    )}
                                                </div>
                                                {pathList && pathList.length > 0 && (
                                                    <div className="compact-history-path">
                                                        {pathList.map((node, index) => {
                                                            const listUrl = getIndexListUrl(indices, node.id);
                                                            return (
                                                                <span key={node.id}>
                                                                    <Link to={listUrl} className="text-muted hover-underline" onClick={onClose}>
                                                                        {node.label}
                                                                    </Link>
                                                                    {index < pathList.length - 1 && <i className="fal fa-chevron-left mx-2" style={{ fontSize: '0.65rem' }}></i>}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="compact-history-right">
                                            <span className="compact-history-badge blue">
                                                <i className="fal fa-eye ml-1"></i>
                                                <span>{lesson.Attempts?.length || 0}</span>
                                            </span>
                                            {lesson.BestMark !== null && lesson.BestMark !== undefined && (
                                                <span className="compact-history-badge green">
                                                    <i className="fal fa-trophy ml-1"></i>
                                                    <span>{lesson.BestMark} / {lesson.TotalMark}</span>
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="compact-history-toggle-btn"
                                                onClick={() => toggleExpanded(lesson.Id || idx)}
                                            >
                                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Collapsible Details */}
                                    <div className={`compact-history-collapsible ${isExpanded ? 'show' : ''}`}>
                                        <div className="compact-history-details-box">
                                            <div className="small text-muted font-weight-bold mb-2">{t("ViewsTimeline") || "تواريخ المشاهدة"}:</div>
                                            <div className="d-flex flex-wrap gap-2 mt-1 justify-content-start">
                                                {lesson.Attempts?.map((attempt, aIdx) => (
                                                    <span key={attempt.Id || aIdx} className="badge badge-light p-2 border border-light">
                                                        <i className="fal fa-clock ml-1 text-info"></i>
                                                        {attempt.CreationDate}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Cancel") || "إلغاء"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const mapStateToProps = (state) => ({
    indices: state.indices
});

export default connect(mapStateToProps, { fetchIndices })(UserLessonHistoryModal);
